import { useQueryClient } from '@tanstack/react-query';

import type { TokenName} from '@shared/constants/wallets';
import { DEFAULT_TARGET_CHAIN, tokensInfo } from '@shared/constants/wallets';
import { numberToWei } from '@shared/helpers/web3/formatters';
import { useWriteContractExecutor } from '@shared/helpers/web3/useWriteContractExecutor';

import { PredictionDirection } from '../../model/PredictionDirection';
import { PREDICTIONS_CONTRACT } from '../config/contract';
import { getQueryKey } from '../config/query';

type Props = {
  amount: number;
  direction: PredictionDirection;
  tokenName?: TokenName;
};

const DEFAULT_TOKEN_NAME: TokenName = 'USDT';

export const usePredictionBet = () => {
  const queryClient = useQueryClient();

  const { execute } = useWriteContractExecutor();

  const betPrediction = async ({ amount, direction, tokenName = DEFAULT_TOKEN_NAME }: Props) => {
    const { decimals, contractsAddresses } = tokensInfo[tokenName];
    const preparedAmount = numberToWei(amount, decimals).toBigInt();

    return execute({
      props: {
        abi: PREDICTIONS_CONTRACT.abi,
        contractAddress: PREDICTIONS_CONTRACT.address,
        functionName: 'bet',
        chainId: DEFAULT_TARGET_CHAIN.id,
        args: [preparedAmount, direction === PredictionDirection.UP ? 0 : 1],
        approve: {
          amount: Number.MAX_SAFE_INTEGER,
          tokenAddress: contractsAddresses.token,
          tokenDecimals: decimals,
          amountToCheck: amount,
        },
      },
      options: {
        toastMessage: 'Processing your prediction...',
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: getQueryKey(),
          });
        },
      },
    });
  };

  return { betPrediction };
};
